import { createFileRoute, Outlet, Link, useLocation, Navigate } from '@tanstack/react-router';
import { useAuth, Skeleton } from '@org/ui';
import { communityTrpc } from '@org/clients/community';
import { Shield, FileWarning, UserX, LayoutDashboard, Pin, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const { t } = useTranslation('community');
  const { user, isLoading: authLoading } = useAuth();
  const location = useLocation();

  const isAdmin = user?.permissions?.includes('admin:all') || user?.permissions?.includes('*');

  // Fetch stats for sidebar badges
  const { data: pendingReports } = communityTrpc.moderation.listReports.useQuery(
    { status: 'pending', limit: 1 },
    { enabled: isAdmin }
  );

  const { data: bannedUsers } = communityTrpc.moderation.listBannedUsers.useQuery(
    { limit: 1 },
    { enabled: isAdmin }
  );

  if (authLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  const navItems = [
    {
      to: '/admin',
      icon: LayoutDashboard,
      label: t('admin.nav.overview'),
      exact: true,
    },
    {
      to: '/admin/moderation',
      icon: FileWarning,
      label: t('admin.nav.moderationQueue'),
      badge: pendingReports?.total,
    },
    {
      to: '/admin/bans',
      icon: UserX,
      label: t('admin.nav.bannedUsers'),
      badge: bannedUsers?.total,
    },
    {
      to: '/admin/default-pins',
      icon: Pin,
      label: t('admin.nav.defaultPins'),
    },
    {
      to: '/admin/moderators',
      icon: Users,
      label: t('admin.nav.moderators'),
    },
  ];

  return (
    <div className="flex min-h-[calc(100vh-8rem)] gap-6">
      {/* Admin Sidebar */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="sticky top-24 rounded-2xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center gap-2 border-b pb-4">
            <Shield className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="font-semibold">{t('admin.nav.panel')}</h2>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = item.exact
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.label}
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-medium text-white">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile nav */}
      <div className="mb-4 flex gap-2 overflow-x-auto lg:hidden">
        {navItems.map((item) => {
          const isActive = item.exact
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);

          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-white'
                  : 'bg-card text-foreground hover:bg-muted'
              }`}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
              {item.badge !== undefined && item.badge > 0 && (
                <span className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium ${
                  isActive ? 'bg-white/20 text-white' : 'bg-primary text-white'
                }`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Main content */}
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
